const ENV_UPLOAD_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_UPLOAD_BASE_URL) || '';

const CACHE_KEY = 'upload_region_cache_v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let memoryCache = null;
let pendingRequest = null;

export const getAuthToken = () => {
  try {
    return window?.localStorage?.getItem('auth_token') || '';
  } catch {
    return '';
  }
};

export const withAuthHeaders = (headers = {}) => {
  const token = getAuthToken();
  if (!token) return headers;
  if (headers.Authorization || headers.authorization) return headers;
  return { ...headers, Authorization: `Bearer ${token}` };
};

export const buildUploadUrl = (path, baseUrl = ENV_UPLOAD_BASE_URL) => {
  if (!baseUrl) return path;
  try {
    return new URL(path, baseUrl).toString();
  } catch (err) {
    console.warn('Invalid upload base URL, falling back to same-origin uploads.', err);
    return path;
  }
};

const readCache = () => {
  try {
    const raw = window?.localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || !parsed?.value) return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

const writeCache = (value) => {
  try {
    window?.localStorage?.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), value }));
  } catch {}
};

export const resolveUploadTarget = async () => {
  if (ENV_UPLOAD_BASE_URL) {
    return { baseUrl: ENV_UPLOAD_BASE_URL, fallbacks: [], source: 'env' };
  }

  if (memoryCache && Date.now() - memoryCache.at < CACHE_TTL_MS) {
    return memoryCache.value;
  }

  const cached = readCache();
  if (cached) {
    memoryCache = { at: Date.now(), value: cached };
    return cached;
  }

  if (!pendingRequest) {
    pendingRequest = (async () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const params = new URLSearchParams();
        if (tz) params.set('tz', tz);
        const url = `/api/uploads/region${params.toString() ? `?${params}` : ''}`;
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: withAuthHeaders()
        });
        if (!res.ok) throw new Error(`Upload region lookup failed: ${res.status}`);
        const data = await res.json();
        const value = {
          baseUrl: data?.base_url || '',
          fallbacks: Array.isArray(data?.fallbacks) ? data.fallbacks : [],
          region: data?.region || null,
          source: data?.source || 'api'
        };
        memoryCache = { at: Date.now(), value };
        writeCache(value);
        return value;
      } catch (err) {
        const value = { baseUrl: '', fallbacks: [], source: 'default' };
        memoryCache = { at: Date.now(), value };
        return value;
      } finally {
        pendingRequest = null;
      }
    })();
  }

  return pendingRequest;
};
